// ============================================================================
// Charge Capture — generates a draft superbill from everything documented on
// the case spine: the procedure CPT, supplies consumed (priced from the
// inventory catalog), medications administered, and implanted devices.
// The draft lands in Forge → Billing for coding review (ICD/CPT completion,
// facility fee) and 837P submission. One superbill per case (DB-enforced).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { DollarSign, Loader, CheckCircle, FileText, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Line {
  type: 'procedure' | 'supply' | 'medication' | 'implant';
  description: string;
  qty: number;
  unit_amount: number;
  amount: number;
  meta?: string;
}

interface Props {
  caseId: number;
  encounterId?: number | null;
}

interface BillingSuggestion {
  suggested_cpt_codes?: { code: string; description?: string; confidence?: number; reason?: string }[];
  suggested_icd_codes?: { code: string; description?: string; confidence?: number }[];
  suggested_modifiers?: string[];
  denial_risk_score?: number;
  denial_risk_label?: string;
  denial_risk_factors?: string[];
  estimated_reimbursement?: number;
  coding_notes?: string;
  error?: string;
}

export function ChargeCapturePanel({ caseId, encounterId }: Props) {
  const [existing, setExisting] = useState<{ superbill_id: number; billing_status: string; total_amount: number } | null>(null);
  const [lines, setLines] = useState<Line[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Billing AI (2026-08-15): invokes the estate billing-ai edge function for
  // this case's encounter. Suggestions persist to cr.billing_ai_suggestions
  // (status 'pending') and are advisory — the biller accepts/rejects in Forge.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [aiResult, setAiResult] = useState<BillingSuggestion | null>(null);

  async function runBillingAi() {
    if (encounterId == null) { setAiNote('No encounter is linked to this case yet — billing AI runs against the encounter record.'); return; }
    setAiBusy(true); setAiNote(''); setAiResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('billing-ai', { body: { encounter_id: encounterId } });
      if (error) throw new Error(error.message ?? 'billing-ai failed');
      if ((data as BillingSuggestion)?.error) throw new Error(String((data as BillingSuggestion).error));
      setAiResult(data as BillingSuggestion);
      setAiNote('Suggestions saved for biller review in Forge → Billing (status: pending).');
    } catch (e) {
      setAiNote(e instanceof Error ? e.message : 'Billing AI unavailable');
    } finally {
      setAiBusy(false);
    }
  }

  const cr = (supabase as any).schema('cr');

  const loadExisting = useCallback(async () => {
    const { data } = await cr.from('superbill')
      .select('superbill_id, billing_status, total_amount')
      .eq('case_id', caseId).maybeSingle();
    setExisting(data ?? null);
  }, [caseId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const buildLines = useCallback(async (): Promise<{ caseRow: any; built: Line[] }> => {
    const { data: caseRow, error: cErr } = await supabase.from('or_cases')
      .select('case_id, org_id, patient_id, case_number, procedure_name, procedure_cpt_code, case_date, scheduled_start')
      .eq('case_id', caseId).single();
    if (cErr || !caseRow) throw new Error('Case not accessible');

    const [{ data: supplies }, { data: meds }] = await Promise.all([
      supabase.from('case_supplies').select('supply_id, supply_name, quantity_used, unit_of_measure').eq('case_id', caseId),
      supabase.from('case_medications').select('medication_id, medication_name, dose, dose_unit').eq('case_id', caseId),
    ]);

    const supplyIds = [...new Set((supplies ?? []).map((s: any) => s.supply_id).filter(Boolean))];
    const medIds = [...new Set((meds ?? []).map((m: any) => m.medication_id).filter(Boolean))];
    const [supCosts, medCosts] = await Promise.all([
      supplyIds.length
        ? supabase.from('or_surgical_supplies').select('supply_id, unit_cost').in('supply_id', supplyIds)
        : Promise.resolve({ data: [] } as any),
      medIds.length
        ? supabase.from('or_medications').select('medication_id, unit_cost').in('medication_id', medIds)
        : Promise.resolve({ data: [] } as any),
    ]);
    const supCost = new Map<string, number>((supCosts.data ?? []).map((r: any) => [r.supply_id, Number(r.unit_cost ?? 0)] as [string, number]));
    const medCost = new Map<string, number>((medCosts.data ?? []).map((r: any) => [r.medication_id, Number(r.unit_cost ?? 0)] as [string, number]));

    const { data: implants } = await cr.from('patient_implantable_devices')
      .select('device_name, udi, lot_number, implant_date')
      .eq('patient_id', caseRow.patient_id).eq('is_active', true);

    const built: Line[] = [];
    built.push({
      type: 'procedure',
      description: `${caseRow.procedure_name}${caseRow.procedure_cpt_code ? ` (CPT ${caseRow.procedure_cpt_code})` : ''} — facility fee`,
      qty: 1, unit_amount: 0, amount: 0,
      meta: 'Facility fee to be set during coding review',
    });
    for (const s of supplies ?? []) {
      const unit = supCost.get(s.supply_id) ?? 0;
      const qty = Number(s.quantity_used ?? 1);
      built.push({ type: 'supply', description: s.supply_name, qty, unit_amount: unit, amount: +(unit * qty).toFixed(2) });
    }
    for (const m of meds ?? []) {
      const unit = medCost.get(m.medication_id) ?? 0;
      built.push({ type: 'medication', description: `${m.medication_name} ${m.dose ?? ''}${m.dose_unit ?? ''}`.trim(), qty: 1, unit_amount: unit, amount: unit });
    }
    const caseDate = (caseRow.case_date ?? caseRow.scheduled_start ?? '').slice(0, 10);
    for (const d of implants ?? []) {
      if (!caseDate || (d.implant_date ?? '').slice(0, 10) === caseDate) {
        built.push({
          type: 'implant',
          description: `Implant: ${d.device_name}${d.lot_number ? ` (lot ${d.lot_number})` : ''}`,
          qty: 1, unit_amount: 0, amount: 0,
          meta: d.udi ? `UDI ${d.udi} — price at coding review` : 'Price at coding review',
        });
      }
    }
    return { caseRow, built };
  }, [caseId]);

  const preview = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const { built } = await buildLines();
      setLines(built);
    } catch (e: any) { setErr(e.message ?? 'Could not build charges'); }
    setBusy(false);
  }, [buildLines]);

  const generate = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const { caseRow, built } = await buildLines();
      const total = +built.reduce((s, l) => s + l.amount, 0).toFixed(2);
      const { error } = await cr.from('superbill').insert({
        org_id: caseRow.org_id,
        patient_id: caseRow.patient_id,
        case_id: caseRow.case_id,
        service_date: (caseRow.case_date ?? caseRow.scheduled_start ?? new Date().toISOString()).slice(0, 10),
        cpt_codes: caseRow.procedure_cpt_code ? [caseRow.procedure_cpt_code] : [],
        procedure_lines: built,
        total_amount: total,
        total_charges: total,
        billing_status: 'draft',
        place_of_service: '24', // Ambulatory Surgical Center
        notes: `Auto-generated from OR case ${caseRow.case_number} (charge capture)`,
      });
      if (error) throw error;
      setLines(null);
      await loadExisting();
    } catch (e: any) { setErr(e.message ?? 'Could not create superbill'); }
    setBusy(false);
  }, [buildLines, loadExisting]);

  return (
    <div className="bg-pt-dark rounded-lg border border-pt-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <DollarSign className="w-5 h-5 text-pt-gold" />
        <h2 className="text-lg font-semibold text-pt-text">Charge Capture</h2>
        {existing && (
          <span className="ml-auto flex items-center gap-2 text-xs font-mono text-green-400">
            <CheckCircle size={13} />
            Superbill #{existing.superbill_id} · {existing.billing_status} · ${Number(existing.total_amount ?? 0).toFixed(2)}
          </span>
        )}
      </div>

      {existing ? (
        <p className="text-sm text-pt-text-muted">
          Charges captured for this case. Coding review, ICD completion, and 837P submission happen in
          <span className="text-pt-gold"> Forge → Billing</span>.
        </p>
      ) : (
        <>
          <p className="text-sm text-pt-text-muted mb-4">
            Builds a draft superbill from the documented procedure, supplies consumed (catalog-priced),
            medications administered, and implants — ready for coding review in Forge Billing.
          </p>
          <div className="flex items-center gap-3">
            <button onClick={preview} disabled={busy}
              className="px-4 py-2 rounded-lg border border-pt-border text-pt-text-muted hover:text-pt-text text-sm disabled:opacity-50">
              {busy && !lines ? <Loader size={14} className="animate-spin inline mr-1" /> : null}
              Preview charges
            </button>
            <button onClick={generate} disabled={busy}
              className="px-4 py-2 rounded-lg bg-pt-gold text-pt-darker font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader size={14} className="animate-spin" /> : <FileText size={14} />}
              Generate superbill
            </button>
          </div>
          {err && <p className="text-sm text-pt-red mt-3">{err}</p>}
          {lines && (
            <table className="w-full text-xs mt-4">
              <thead>
                <tr className="text-pt-text-dim uppercase tracking-wider text-[10px] text-left">
                  <th className="py-1.5 pr-2">Type</th><th className="py-1.5 pr-2">Description</th>
                  <th className="py-1.5 pr-2">Qty</th><th className="py-1.5 pr-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-pt-border/40 text-pt-text">
                    <td className="py-1.5 pr-2 capitalize text-pt-text-dim">{l.type}</td>
                    <td className="py-1.5 pr-2">{l.description}{l.meta && <span className="text-pt-text-dim"> — {l.meta}</span>}</td>
                    <td className="py-1.5 pr-2">{l.qty}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">${l.amount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="border-t border-pt-border font-semibold text-pt-gold">
                  <td colSpan={3} className="py-2 pr-2 text-right">Draft total</td>
                  <td className="py-2 pr-2 text-right font-mono">
                    ${lines.reduce((s, l) => s + l.amount, 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </>
      )}

      <div className="mt-4 rounded-xl border border-violet-500/30 bg-violet-500/[0.04] p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Sparkles size={15} className="text-violet-300" />
          <div className="mr-auto">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-pt-text">Billing AI coding suggestions</h4>
              <span className="text-[9px] uppercase tracking-[0.14em] rounded border border-violet-400/40 bg-violet-500/10 text-violet-300 px-1.5 py-0.5">AI · biller reviews</span>
            </div>
            <p className="text-[10px] text-pt-text-muted">CPT/ICD/modifier suggestions and denial risk from the documented encounter. Advisory only — the biller accepts or rejects in Forge.</p>
          </div>
          <button type="button" onClick={() => void runBillingAi()} disabled={aiBusy}
            className="text-xs rounded border border-violet-400/40 text-violet-200 px-3 py-1.5 hover:bg-violet-500/10 disabled:opacity-50">
            {aiBusy ? 'Analyzing encounter…' : aiResult ? 'Regenerate' : 'Suggest coding'}
          </button>
        </div>
        {aiNote && <p className="mt-2 text-[11px] text-pt-text-muted">{aiNote}</p>}
        {aiResult && (
          <div className="mt-3 space-y-2 text-xs text-pt-text">
            {!!aiResult.suggested_cpt_codes?.length && (
              <div><span className="text-pt-text-muted">CPT:</span> {aiResult.suggested_cpt_codes.map((c) => `${c.code}${c.description ? ` (${c.description})` : ''}`).join(', ')}</div>
            )}
            {!!aiResult.suggested_icd_codes?.length && (
              <div><span className="text-pt-text-muted">ICD-10:</span> {aiResult.suggested_icd_codes.map((c) => `${c.code}${c.description ? ` (${c.description})` : ''}`).join(', ')}</div>
            )}
            {!!aiResult.suggested_modifiers?.length && (
              <div><span className="text-pt-text-muted">Modifiers:</span> {aiResult.suggested_modifiers.join(', ')}</div>
            )}
            {aiResult.denial_risk_label && (
              <div><span className="text-pt-text-muted">Denial risk:</span> {aiResult.denial_risk_label}{aiResult.denial_risk_score != null ? ` (${aiResult.denial_risk_score}/10)` : ''}{aiResult.denial_risk_factors?.length ? ` — ${aiResult.denial_risk_factors.join('; ')}` : ''}</div>
            )}
            {aiResult.coding_notes && <p className="text-pt-text-dim">{aiResult.coding_notes}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
